import { useCallback } from "react";

import {
  useCellReviewStore,
  type CellReviewFlagType,
  type CellReviewIdentityRequest,
  type CellReviewResponseRecord,
  type CellReviewStoreResult,
} from "../shared/cellReviews/useCellReviewStore";
import { cellReviewsQueryKey } from "../shared/cellReviews/fetchCellReviews";

/**
 * Splits a Comps `fieldPath` into its persisted parts.
 *
 * `fieldPath` is `${planGroupId}:${rowType}`. `planGroupId` is an opaque id
 * string (not guaranteed a UUID — e.g. synthesized placeholder ids), so the row
 * type is taken as the last `:`-separated segment and the planGroupId is
 * everything before it, which stays correct even if the id itself has colons.
 */
const parseFieldPath = (
  fieldPath: string,
): {
  planGroupId: string;
  rowType: string;
} => {
  const parts = fieldPath.split(":");
  const rowType = parts.pop() ?? "";
  const planGroupId = parts.join(":");
  return { planGroupId, rowType };
};

/** Unique client-side key for a cell review: column key + fieldPath.
 * Mirrors `reviewKey(planId, fieldPath)` in the store, reconstructed from the
 * persisted column (benefitKey) and row identity: `coverageType` holds the
 * planGroupId and `network` holds the rowType, so this returns
 * `${planId}::${planGroupId}:${rowType}`. */
const identityKeyOf = (review: CellReviewResponseRecord): string =>
  `${review.benefitKey}::${review.coverageType}:${review.network ?? ""}`;

interface UseCompsCellReviewsOptions {
  rfpId: string | undefined;
  enabled?: boolean;
  onSaveError?: (error: Error) => void;
}

/**
 * Adapts the shared cell-review store to the Comps grid, where a cell has no
 * proposal plan — it is identified by its column (carrierId / "requested" /
 * "incumbent") and coverage row instead. Persists under the plan-independent
 * "comps" scope so disabled cells and the Requested/Incumbent columns are
 * reviewable, mirroring how the Responses tab works.
 *
 * `planId` is the column key and `fieldPath` is the Comps row identifier
 * (`${rowGroupId}:${rowType}`, e.g. "<planGroupId>:type"), which scopes the
 * review to a specific coverage row (and rowType) within a column. This lets
 * the two rows of a coverage be reviewed independently.
 */
export const useCompsCellReviews = ({
  rfpId,
  enabled = true,
  onSaveError,
}: UseCompsCellReviewsOptions): CellReviewStoreResult => {
  const buildIdentity = useCallback(
    (
      planId: string,
      fieldPath: string,
      reviewType: CellReviewFlagType,
    ): CellReviewIdentityRequest => {
      const { planGroupId, rowType } = parseFieldPath(fieldPath);
      return {
        scope: { type: "comps", rfpId },
        coverageType: planGroupId,
        benefitKey: planId,
        propertyKey: "",
        network: rowType,
        reviewType,
      };
    },
    [rfpId],
  );

  const cellReviewsPath = rfpId
    ? `/rfps/${encodeURIComponent(rfpId)}/cell-reviews?scopeType=comps`
    : undefined;

  return useCellReviewStore({
    rfpId,
    queryKey: cellReviewsQueryKey("comps", rfpId),
    listUrl: cellReviewsPath,
    mutateUrl: cellReviewsPath,
    enabled,
    identityKeyOf,
    buildIdentity,
    onSaveError,
  });
};
